'use client'

import { useEffect } from 'react'

/**
 * Dismiss an open `<details>` on an outside press or Escape — the two things the
 * native element does not do for you, and the reason a plain `<details>` otherwise
 * feels broken next to a real menu: it stays open until you press the trigger again.
 *
 * **This is not the hover-open layer the account menu forbids.** That trap is a
 * *hover-open* listener racing the primitive's own `pointerdown` toggle, which puts
 * opening out of phase and makes the next press close what the pointer's arrival had
 * just opened. Nothing here touches opening, and an outside press is by definition not
 * on the trigger, so there is no interaction to race.
 *
 * The element's own `open` attribute stays the single source of truth — no mirrored
 * React state — so the server still renders the whole panel and its contents remain in
 * the served markup. Listeners are attached only while the panel is open, since a closed
 * picker on every page of the site should not hold document-level handlers.
 */
export function useDismissOnOutsidePress(ref: React.RefObject<HTMLDetailsElement | null>) {
    useEffect(() => {
        const details = ref.current
        if (!details) {
            return
        }

        const close = () => {
            details.open = false
        }

        const onPointerDown = (event: PointerEvent) => {
            if (event.target instanceof Node && !details.contains(event.target)) {
                close()
            }
        }

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                close()
                // Focus would otherwise be left on a panel that no longer exists.
                details.querySelector('summary')?.focus()
            }
        }

        // Called as methods on `document`, not extracted into a variable — an
        // unbound `addEventListener` loses its receiver and throws.
        const listen = (add: boolean) => {
            if (add) {
                document.addEventListener('pointerdown', onPointerDown as EventListener)
                document.addEventListener('keydown', onKeyDown as EventListener)
                return
            }
            document.removeEventListener('pointerdown', onPointerDown as EventListener)
            document.removeEventListener('keydown', onKeyDown as EventListener)
        }

        // `toggle` fires for programmatic changes too, so closing above unbinds here.
        const onToggle = () => listen(details.open)

        details.addEventListener('toggle', onToggle)
        return () => {
            details.removeEventListener('toggle', onToggle)
            listen(false)
        }
    }, [ref])
}
